import { useId, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { GridCol } from 'dibs-elements/exports/Grid';
import { SeoLink } from 'dibs-seo/exports/SeoLink';

import HpSharedModuleItemTile from '../HpSharedModuleItemTile/HpSharedModuleItemTile';
import HpLazyImageWrapper from '../HpLazyImageWrapper/HpLazyImageWrapper';
import ResponsiveImage from '../ResponsiveImage';
import { HpSharedPromoHeroItem } from '../HpSharedSharedHeroBanner/HpSharedSharedHeroBanner';

import styles from './HpSharedPromoModulePromoItem.scss';

import { type HpSharedPromoModulePromoItem_promotion$key } from './__generated__/HpSharedPromoModulePromoItem_promotion.graphql';

type Props = {
    promotion: HpSharedPromoModulePromoItem_promotion$key;
    onClick?: () => void;
    useLazyLoadImages: boolean;
    index?: number;
    isSinglePromo: boolean;
    isMobile: boolean;
    widthPercentage: number;
};

const HpSharedPromoModulePromoItem: FC<Props> = ({
    promotion: promotionRef,
    onClick = () => {},
    useLazyLoadImages,
    index,
    isSinglePromo,
    isMobile,
    widthPercentage,
}) => {
    const promotion = useFragment(
        graphql`
            fragment HpSharedPromoModulePromoItem_promotion on PromoItem {
                imageCopy
                cta
                isFullBleedImage
                title
                description
                format
                linkData {
                    ...HpSharedModuleItemTile_linkData
                    ...SeoLink_linkData
                }
                ... on PromoItem @skip(if: $isMobile) {
                    imageUrl
                    imageHeight
                    imageWidth
                }
                ... on PromoItem @include(if: $isMobile) {
                    mobileImageUrl
                    mobileImageHeight
                    mobileImageWidth
                }
                ...HpSharedSharedHeroBanner_promoItem
            }
        `,
        promotionRef
    );

    const {
        mobileImageUrl,
        imageUrl,
        linkData,
        imageHeight,
        imageWidth,
        mobileImageHeight,
        mobileImageWidth,
        imageCopy,
        cta,
        title,
        description,
        isFullBleedImage,
        format,
    } = promotion || {};

    const promoItemId = `promomodulepromo-${useId()}`;

    if (!linkData) {
        return null;
    }

    const shownImageUrl = imageUrl || mobileImageUrl;
    const shownImageWidth = mobileImageWidth || imageWidth;
    const shownImageHeight = mobileImageHeight || imageHeight;
    const isFullBleed = isFullBleedImage && isSinglePromo;

    return (
        <GridCol
            tabletLandscape={isSinglePromo ? 12 : 6}
            desktop={isSinglePromo ? 12 : Math.round(12 * widthPercentage)}
        >
            {imageCopy && (
                <span id={promoItemId} className={styles.imageCopy}>
                    {imageCopy}
                </span>
            )}
            {isSinglePromo ? (
                <>
                    {format === 'CAPTION' ? (
                        <div className={styles.fullWidth}>
                            <HpSharedPromoHeroItem
                                isVisible
                                useLazyLoadImages={useLazyLoadImages}
                                onClick={onClick}
                                promoItem={promotion}
                            />
                        </div>
                    ) : (
                        <SeoLink
                            dataTn="promo-module-item"
                            className={styles.link}
                            linkData={linkData}
                            onClick={onClick}
                            ariaLabel={cta || undefined}
                            ariaDescribedBy={imageCopy ? promoItemId : undefined}
                        >
                            <HpLazyImageWrapper
                                useLazyLoad={useLazyLoadImages}
                                imageHeight={shownImageHeight}
                                imageWidth={shownImageWidth}
                            >
                                <ResponsiveImage
                                    ariaHidden
                                    className={classnames(styles.image, {
                                        [styles.isFullBleed]: isFullBleed,
                                    })}
                                    src={shownImageUrl || ''}
                                    srcSetWidths={[400, 640, 960, 1280, 1440]}
                                />
                            </HpLazyImageWrapper>
                        </SeoLink>
                    )}
                </>
            ) : (
                <div className={styles.item}>
                    <HpSharedModuleItemTile
                        linkData={linkData}
                        dataTn={`editorial-link-${index}`}
                        onClick={onClick}
                        title={title}
                        description={description}
                        cta={!isMobile ? cta : undefined}
                        imgSrcSetWidths={[400, 640, 800]}
                        imgSizes="(max-width:768px) 95vw, 50vw"
                        imageUrl={shownImageUrl}
                        imageWidth={shownImageWidth}
                        imageHeight={shownImageHeight}
                        imageClass={styles.tileImage}
                        useLazyLoadImages={useLazyLoadImages}
                        headerClass={styles.header}
                    />
                </div>
            )}
        </GridCol>
    );
};

export default HpSharedPromoModulePromoItem;
